import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { AlertCircle, CalendarDays, Users, X } from "lucide-react";

import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";

/**
 * The attendee's own ticket. Public — the code is the credential, and it only
 * reveals what a door volunteer would see anyway.
 *
 * Built to work in a badly lit hallway on a cracked phone:
 *   - the QR is large and high-contrast, with a wide quiet zone
 *   - the code is printed underneath in mono, because cameras fail and people
 *     read it out instead
 *   - light background is forced; a dark-themed QR does not scan reliably
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TicketPage() {
  const { code } = useParams();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelled, setCancelled] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  // "I can't come any more" previously had no path, so a no-show stayed in the
  // headcount that drives quantities and cost per head.
  const cancel = async () => {
    if (!window.confirm("Cancel your registration for this event?")) return;
    setCancelling(true);
    try {
      await axios.post(`${API}/public/ticket/${code}/cancel`);
      setCancelled(true);
    } catch (err) {
      window.alert(err?.response?.data?.detail || "Could not cancel. Please contact the organisers.");
    } finally {
      setCancelling(false);
    }
  };

  useEffect(() => {
    axios.get(`${API}/public/ticket/${code}`)
      .then((res) => setTicket(res.data))
      .catch(() => setTicket(null))
      .finally(() => setLoading(false));
  }, [code]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white p-4">
        <div className="max-w-sm mx-auto pt-12 h-80 bg-neutral-100 rounded-xl animate-pulse" />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6">
        <div className="text-center space-y-3" data-testid="ticket-not-found">
          <AlertCircle className="h-10 w-10 mx-auto text-neutral-400" />
          <p className="font-medium text-neutral-900">Ticket not found</p>
          <p className="text-sm text-neutral-500">
            Check the code, or register again using the event link.
          </p>
        </div>
      </div>
    );
  }

  if (cancelled) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6">
        <div className="text-center space-y-3 max-w-sm" data-testid="ticket-cancelled">
          <X className="h-10 w-10 mx-auto text-neutral-400" aria-hidden="true" />
          <p className="font-medium text-neutral-900">Registration cancelled</p>
          <p className="text-sm text-neutral-500">
            Thanks for letting us know — it helps us order the right amount of food.
            You can register again from the event link if plans change.
          </p>
        </div>
      </div>
    );
  }

  return (
    // Forced light: scanners struggle with inverted QR codes.
    <div className="min-h-screen bg-white p-4">
      <div className="max-w-sm mx-auto pt-10 space-y-5 text-center">
        <div>
          <h1 className="text-lg font-bold text-neutral-900">{ticket.event_title}</h1>
          <p className="text-sm text-neutral-500">Entry ticket</p>
        </div>

        <Card className="border-neutral-200 shadow-sm">
          <CardContent className="p-6 space-y-4">
            <div className="bg-white p-4 inline-block rounded-lg" data-testid="ticket-qr">
              <QRCodeSVG value={ticket.code} size={200} level="M" includeMargin />
            </div>
            <div>
              <p className="text-3xl font-mono font-bold tracking-[0.2em] text-neutral-900"
                 data-testid="ticket-code">
                {ticket.code}
              </p>
              <p className="text-xs text-neutral-500 mt-1">
                Show this code at the door
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-1 text-sm text-neutral-700">
          <p className="font-semibold" data-testid="ticket-name">{ticket.name}</p>
          {ticket.party_size > 1 && (
            <p className="flex items-center justify-center gap-1.5 text-neutral-500">
              <Users className="h-3.5 w-3.5" />{ticket.party_size} people
            </p>
          )}
          {ticket.days?.length > 0 && (
            <p className="flex items-center justify-center gap-1.5 text-neutral-500">
              <CalendarDays className="h-3.5 w-3.5" />
              Day{ticket.days.length > 1 ? "s" : ""} {ticket.days.join(", ")}
            </p>
          )}
          {ticket.dietary?.length > 0 && (
            <p className="text-neutral-500">{ticket.dietary.join(" · ")}</p>
          )}
        </div>

        <Button variant="ghost" size="sm" onClick={cancel} disabled={cancelling}
                className="text-neutral-500 h-11" data-testid="ticket-cancel">
          {cancelling ? "Cancelling…" : "Can't make it? Cancel registration"}
        </Button>
      </div>
    </div>
  );
}
